import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import {
  Badge,
  Card,
  CardHeader,
  ErrorState,
  LoadingState,
  PageHeader,
} from '@/components/ui';
import type { Roadmap } from './types';

const phases = [
  {
    key: 'foundation',
    title: '1. Fix the foundation',
    description:
      'Clear critical and indexability problems before investing in growth.',
  },
  {
    key: 'quick_wins',
    title: '2. Capture quick wins',
    description:
      'Review low-effort, low-risk improvements with supporting audit evidence.',
  },
  {
    key: 'growth',
    title: '3. Build lasting growth',
    description:
      'Plan content and structural work around the site’s audience and observed queries.',
  },
];

export function SeoRoadmapPage() {
  const { siteId = '' } = useParams();
  const path = `/sites/${siteId}`;
  const query = useQuery({
    queryKey: ['seo-roadmap', siteId],
    queryFn: () => api.get<Roadmap>(`${path}/seo-roadmap`),
    staleTime: 60_000,
  });
  if (query.isLoading) return <LoadingState label="Prioritizing SEO work" />;
  if (query.isError)
    return (
      <ErrorState
        message={query.error.message}
        onRetry={() => void query.refetch()}
      />
    );
  const data = query.data;
  if (!data) return null;
  return (
    <div className="space-y-5">
      <PageHeader
        title="SEO roadmap"
        description="A sequenced repair and growth plan grounded in your latest completed audit, crawl, and search performance."
        actions={
          <Link
            className="text-sm font-medium text-brand"
            to={`${path}/keywords`}
          >
            Keyword research →
          </Link>
        }
      />
      <Card className="roadmap-workflow-card">
        <CardHeader title="From recommendation to verified repair" />
        <ol className="grid gap-4 text-sm text-ink-muted sm:grid-cols-2 xl:grid-cols-5">
          {data.workflow.map((step, i) => (
            <li key={step}>
              <span className="mb-2 block text-lg font-semibold text-brand">
                0{i + 1}
              </span>
              {step}
            </li>
          ))}
        </ol>
        <div className="mt-4 flex gap-4 text-sm">
          <Link className="text-brand" to={`${path}/analysis`}>
            Run expert AI analysis
          </Link>
          <Link className="text-brand" to={`${path}/actions`}>
            Review repair queue
          </Link>
        </div>
      </Card>
      {!data.audit_id ? (
        <Card>
          <p className="text-sm text-ink-muted">
            Complete a crawl and audit to generate a prioritized repair plan.
          </p>
          <Link className="mt-2 inline-block text-sm text-brand" to={path}>
            Open website overview →
          </Link>
        </Card>
      ) : null}
      {phases.map((phase) => {
        const items = data.items.filter((item) => item.phase === phase.key);
        return (
          <Card key={phase.key} className={`roadmap-phase phase-${phase.key}`}>
            <CardHeader title={phase.title} description={phase.description} />
            <div className="space-y-4">
              {items.slice(0, 20).map((item) => (
                <div key={item.id} className="border-t border-line pt-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <h3 className="text-sm font-semibold">{item.title}</h3>
                    <div className="flex gap-2">
                      <Badge>Priority {item.priority_score.toFixed(0)}</Badge>
                      <Badge>{item.risk.toLowerCase()} risk</Badge>
                      <Badge>
                        {Math.round(item.confidence * 100)}% confidence
                      </Badge>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-ink-muted">{item.reason}</p>
                  <p className="mt-2 text-xs text-ink-muted">
                    {item.observed_clicks_on_affected_pages} observed clicks on
                    affected pages · {item.effort} effort
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <Badge>
                      {item.ready_for_proposal
                        ? 'Values ready for review'
                        : 'Editorial input needed'}
                    </Badge>
                    <Link
                      className="text-xs font-medium text-brand"
                      to={`${path}/opportunities`}
                    >
                      Review recommendation →
                    </Link>
                  </div>
                  <p className="mt-2 text-xs text-ink-muted">
                    {item.next_step}
                  </p>
                  {item.affected_urls.length ? (
                    <details className="mt-2 text-xs text-ink-muted">
                      <summary className="cursor-pointer">
                        Affected pages
                      </summary>
                      {item.affected_urls.map((url, i) => (
                        <p className="mt-1 break-all" key={i}>
                          {url}
                        </p>
                      ))}
                    </details>
                  ) : null}
                </div>
              ))}
              {!items.length ? (
                <p className="text-sm text-ink-muted">
                  No outstanding recommendations in this phase.
                </p>
              ) : null}
              {items.length > 20 ? (
                <Link
                  className="text-sm text-brand"
                  to={`${path}/opportunities`}
                >
                  View all {items.length} recommendations →
                </Link>
              ) : null}
            </div>
          </Card>
        );
      })}
      <Card>
        <CardHeader
          title="Internal link opportunities"
          description="Related pages that could help readers find useful content. Review context and destination before adding a link."
        />
        <div className="grid gap-4 md:grid-cols-2">
          {data.internal_link_opportunities.map((idea, i) => (
            <div key={i} className="rounded-lg border border-line p-4">
              <p className="break-all text-xs text-ink-muted">
                From: {idea.source_url}
              </p>
              <p className="mt-2 break-all text-xs text-ink-muted">
                To: {idea.target_url}
              </p>
              <p className="mt-3 text-sm font-medium">
                {idea.suggested_anchor}
              </p>
              <p className="mt-1 text-xs text-ink-muted">{idea.reason}</p>
              <Badge className="mt-3">Editorial review required</Badge>
            </div>
          ))}
        </div>
        {!data.internal_link_opportunities.length ? (
          <p className="text-sm text-ink-muted">
            No supported link candidates in the loaded crawl. Broader page
            coverage may reveal more opportunities.
          </p>
        ) : null}
      </Card>
      <details className="text-xs text-ink-muted">
        <summary className="cursor-pointer">How this plan is scored</summary>
        <ul className="mt-2 list-disc space-y-2 pl-5">
          {data.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
          <li>
            Read-only planning: no website changes occur until an approved
            action is executed.
          </li>
          <li>
            Coverage: up to 100 recommendations, 2,000 pages, and 20,000 crawl
            links. Link candidates use the 40 least-linked destinations and up
            to 300 source pages.
          </li>
        </ul>
      </details>
    </div>
  );
}
